/**
 * Book-level research library. Index lives in book.metadata.research;
 * binaries live in .sbk assets/ and (when synced) S3.
 */

export const RESEARCH_ASSET_KINDS = [
  "image",
  "pdf",
  "document",
  "spreadsheet",
  "webpage",
  "chat_summary",
  "note",
] as const;

export type ResearchAssetKind = (typeof RESEARCH_ASSET_KINDS)[number];

export const RESEARCH_ASSET_KIND_LABELS: Record<ResearchAssetKind, string> = {
  image: "Image",
  pdf: "PDF",
  document: "Document",
  spreadsheet: "Spreadsheet",
  webpage: "Webpage",
  chat_summary: "Chat summary",
  note: "Note",
};

export const EXTRACTED_TEXT_MAX_CHARS = 80_000;
export const RESEARCH_BLOB_MAX_BYTES = 1_500_000;

export type ResearchAsset = {
  id: string;
  kind: ResearchAssetKind;
  title: string;
  mimeType?: string;
  sourceUrl?: string;
  /** Path in the .sbk zip (`assets/<id>`) or cloud S3 key. */
  storageKey: string;
  extractedText?: string;
  notes?: string;
  tags?: string[];
  /** Small image preview only — never a PDF/doc payload. */
  previewDataUrl?: string;
  createdAt: string;
  updatedAt: string;
};

export type ResearchState = {
  assets: ResearchAsset[];
};

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return `${prefix}-${c.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function isResearchAssetKind(value: unknown): value is ResearchAssetKind {
  return typeof value === "string" && (RESEARCH_ASSET_KINDS as readonly string[]).includes(value);
}

export function clipExtractedText(text: string, max = EXTRACTED_TEXT_MAX_CHARS): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}\n\n[truncated]`;
}

export function emptyResearchState(): ResearchState {
  return { assets: [] };
}

export function getResearchState(metadata: Record<string, unknown> | null | undefined): ResearchState {
  const raw = metadata?.research;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyResearchState();
  const rec = raw as Record<string, unknown>;
  const assets = Array.isArray(rec.assets)
    ? rec.assets.map(normalizeResearchAsset).filter((a): a is ResearchAsset => a != null)
    : [];
  return { assets };
}

export function withResearchInMetadata(
  metadata: Record<string, unknown>,
  state: ResearchState
): Record<string, unknown> {
  return { ...metadata, research: { assets: state.assets } };
}

export function normalizeResearchAsset(raw: unknown): ResearchAsset | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : "";
  if (!id) return null;
  const kind = isResearchAssetKind(rec.kind) ? rec.kind : "note";
  const title = typeof rec.title === "string" && rec.title.trim() ? rec.title.trim() : "Untitled";
  const storageKey =
    typeof rec.storageKey === "string" && rec.storageKey.trim()
      ? rec.storageKey.trim()
      : `assets/${id}`;
  return {
    id,
    kind,
    title,
    mimeType: typeof rec.mimeType === "string" ? rec.mimeType : undefined,
    sourceUrl: typeof rec.sourceUrl === "string" ? rec.sourceUrl : undefined,
    storageKey,
    extractedText: typeof rec.extractedText === "string" ? rec.extractedText : undefined,
    notes: typeof rec.notes === "string" ? rec.notes : undefined,
    tags: Array.isArray(rec.tags)
      ? rec.tags.filter((t): t is string => typeof t === "string" && t.trim().length > 0)
      : undefined,
    previewDataUrl: typeof rec.previewDataUrl === "string" ? rec.previewDataUrl : undefined,
    createdAt: typeof rec.createdAt === "string" ? rec.createdAt : nowIso(),
    updatedAt: typeof rec.updatedAt === "string" ? rec.updatedAt : nowIso(),
  };
}

export function createResearchAsset(
  input: Partial<ResearchAsset> & Pick<ResearchAsset, "kind" | "title">
): ResearchAsset {
  const id = input.id?.trim() || newId("asset");
  const at = nowIso();
  return {
    id,
    kind: input.kind,
    title: input.title.trim() || "Untitled",
    mimeType: input.mimeType,
    sourceUrl: input.sourceUrl,
    storageKey: input.storageKey?.trim() || `assets/${id}`,
    extractedText: input.extractedText ? clipExtractedText(input.extractedText) : undefined,
    notes: input.notes,
    tags: input.tags,
    previewDataUrl: input.previewDataUrl,
    createdAt: input.createdAt ?? at,
    updatedAt: input.updatedAt ?? at,
  };
}

export function upsertResearchAsset(state: ResearchState, asset: ResearchAsset): ResearchState {
  const next = { ...asset, updatedAt: nowIso() };
  const index = state.assets.findIndex((a) => a.id === asset.id);
  if (index < 0) return { assets: [...state.assets, next] };
  const assets = [...state.assets];
  assets[index] = { ...assets[index], ...next, createdAt: assets[index].createdAt };
  return { assets };
}

export function removeResearchAsset(state: ResearchState, assetId: string): ResearchState {
  return { assets: state.assets.filter((a) => a.id !== assetId) };
}

export function kindFromMimeType(mimeType: string, fileName = ""): ResearchAssetKind {
  const mime = mimeType.toLowerCase();
  const name = fileName.toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    mime.includes("spreadsheet") ||
    mime.includes("excel") ||
    name.endsWith(".xlsx") ||
    name.endsWith(".xls") ||
    name.endsWith(".csv")
  ) {
    return "spreadsheet";
  }
  if (
    mime.includes("word") ||
    mime.includes("document") ||
    name.endsWith(".doc") ||
    name.endsWith(".docx") ||
    name.endsWith(".rtf") ||
    name.endsWith(".odt")
  ) {
    return "document";
  }
  if (mime.startsWith("text/") || name.endsWith(".txt") || name.endsWith(".md")) return "note";
  return "document";
}

export function searchResearchAssets(
  assets: ResearchAsset[],
  query: string,
  limit = 8
): ResearchAsset[] {
  const q = query.trim().toLowerCase();
  if (!q) return assets.slice(0, limit);
  const scored = assets
    .map((asset) => {
      const hay = [asset.title, asset.kind, ...(asset.tags ?? []), asset.notes ?? "", asset.extractedText ?? ""]
        .join("\n")
        .toLowerCase();
      const idx = hay.indexOf(q);
      return { asset, idx };
    })
    .filter((row) => row.idx >= 0)
    .sort((a, b) => a.idx - b.idx);
  return scored.slice(0, limit).map((row) => row.asset);
}

export function formatResearchCatalogForChat(assets: ResearchAsset[], limit = 20): string {
  if (!assets.length) return "";
  const lines = assets.slice(0, limit).map((asset) => {
    const tags = asset.tags?.length ? ` [${asset.tags.join(", ")}]` : "";
    return `- ${asset.id} · ${RESEARCH_ASSET_KIND_LABELS[asset.kind]} · ${asset.title}${tags}`;
  });
  return [
    "Research library (cite by title or id; call get_research_asset for full extracted text):",
    ...lines,
  ].join("\n");
}

export function formatResearchAssetForChat(asset: ResearchAsset): string {
  const header = `${RESEARCH_ASSET_KIND_LABELS[asset.kind]}: ${asset.title}${
    asset.sourceUrl ? ` (${asset.sourceUrl})` : ""
  }`;
  const notes = asset.notes?.trim() ? `\nNotes:\n${asset.notes.trim()}` : "";
  const body = asset.extractedText?.trim()
    ? `\n\n${clipExtractedText(asset.extractedText, 12_000)}`
    : "\n\n(No extracted text stored for this asset.)";
  return `${header}${notes}${body}`;
}

export function researchImageSrcMap(assets: ResearchAsset[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const asset of assets) {
    if (asset.kind === "image" && asset.previewDataUrl) {
      map[asset.id] = asset.previewDataUrl;
    }
  }
  return map;
}

export function dataUrlToUint8Array(dataUrl: string): Uint8Array | null {
  const match = /^data:([^;]+);base64,([\s\S]+)$/.exec(dataUrl.trim());
  if (!match) return null;
  const b64 = match[2].replace(/\s/g, "");
  try {
    if (typeof Buffer !== "undefined") {
      return new Uint8Array(Buffer.from(b64, "base64"));
    }
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

export function uint8ArrayToDataUrl(bytes: Uint8Array, mimeType = "application/octet-stream"): string {
  const mime = mimeType || "application/octet-stream";
  if (typeof Buffer !== "undefined") {
    return `data:${mime};base64,${Buffer.from(bytes).toString("base64")}`;
  }
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return `data:${mime};base64,${btoa(binary)}`;
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .trim();
}

function htmlToExcerpt(html: string): string {
  const withoutScripts = html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, " ");
  const text = withoutScripts
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return clipExtractedText(text, 20_000);
}

export function assertPublicHttpUrl(raw: string): URL {
  const parsed = new URL(raw.trim());
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error("Only http and https URLs are allowed.");
  }
  const host = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "0.0.0.0" ||
    host === "::1" ||
    host === "127.0.0.1"
  ) {
    throw new Error("That URL is not allowed.");
  }
  if (/^(10|127|0)\.|^(192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(host)) {
    throw new Error("That URL is not allowed.");
  }
  return parsed;
}

export type WebpageResearchResult = {
  title: string;
  extractedText: string;
  sourceUrl: string;
};

export async function fetchWebpageResearch(url: string): Promise<WebpageResearchResult> {
  const parsed = assertPublicHttpUrl(url);
  const sourceUrl = parsed.toString();
  const fallbackTitle = guessWebpageTitle(sourceUrl);
  const res = await fetch(sourceUrl, {
    redirect: "follow",
    signal: AbortSignal.timeout(10_000),
    headers: { Accept: "text/html,application/xhtml+xml,text/plain;q=0.8" },
  });
  if (!res.ok) {
    return { title: fallbackTitle, extractedText: "", sourceUrl };
  }
  const html = await res.text();
  const titleMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const title = decodeHtmlEntities(titleMatch?.[1] || "") || fallbackTitle;
  const meta =
    html.match(/<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["']/i) ||
    html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+name=["']description["']/i);
  const description = decodeHtmlEntities(meta?.[1] || "");
  const excerpt = htmlToExcerpt(html);
  const extractedText = clipExtractedText([description, excerpt].filter(Boolean).join("\n\n"));
  return { title, extractedText, sourceUrl };
}

export function guessWebpageTitle(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    const path = decodeURIComponent(parsed.pathname.replace(/\/+$/, "")).split("/").filter(Boolean);
    const last = path[path.length - 1]?.replace(/[-_]+/g, " ");
    return last ? `${last} — ${host}` : host;
  } catch {
    return url;
  }
}
